// Vendor
// Config
import {blue, green, grey} from 'isee/config/colors';

// Config

// Services
import ApolloService from 'isee/services/apollo/apollo';
import I18nextService from 'isee/services/i18next/i18next';
import ReduxService from 'isee/services/redux/redux';

// Route Components
import AuthenticatedRoute from 'isee/ui/authenticated';

// Shared Components
import asyncComponent from 'isee/ui/components/async-component';
import ConfirmationPrompt from 'isee/ui/components/confirmation-prompt';
import RotateDevicePlaceholder from 'isee/ui/components/rotate-device-placeholder';
import NotFound from 'isee/ui/not-found';

// Theme
import {red} from 'material-ui/colors';
import {createMuiTheme, MuiThemeProvider} from 'material-ui/styles';

// Vendor Components
import withStyles, {StyleRulesCallback, WithStyles} from 'material-ui/styles/withStyles';
import * as React from 'react';
import {ApolloProvider} from 'react-apollo';
import {I18nextProvider} from 'react-i18next';
import {Provider as ReduxProvider} from 'react-redux';
import {BrowserRouter as Router, Route, Switch} from 'react-router-dom';
// Components
const SessionRoute = asyncComponent(() => import('isee/ui/session'));
const GuestRoute = asyncComponent(() => import('isee/ui/guest'));

// Types
type ConfirmationCallback = (ok: boolean) => void;

interface Props {}

type EnhancedProps = Props & WithStyles;

interface State {
  confirmationCallback: ConfirmationCallback;
  confirmationMessage: string;
  isConfirmationPromptOpen: boolean;
}

// tslint:disable:object-literal-sort-keys
const theme = createMuiTheme({
  palette: {
    error: {
      light: red[300],
      main: red[500],
      dark: red[700]
    },
    primary: {
      light: blue[300],
      main: blue[500],
      dark: blue[700]
    },
    secondary: {
      light: green[300],
      main: green[500],
      dark: green[700]
    }
  },
  typography: {
    display1: {
      color: blue[500],
      fontSize: 32
    },
    title: {
      color: blue[900],
      fontSize: 18,
      lineHeight: 1.5,
      fontWeight: 500,
      letterSpacing: 0.3
    },
    subheading: {
      color: grey[500],
      fontSize: 15,
      lineHeight: 1.4
    }
  }
});
// tslint:enable:object-literal-sort-keys

const styles: StyleRulesCallback = () =>
  ({
    '@global': {
      '*': {
        boxSizing: 'border-box'
      },

      'html, body, #root': {
        height: '100%',
        margin: 0,
        overflow: 'hidden',
        width: '100%'
      },

      body: {
        '-moz-osx-font-smoothing': 'grayscale',
        '-webkit-font-smoothing': 'antialiased',
        background: blue[50],
        fontFamily: 'Roboto, sans-serif'
      }
    }
  } as any);

export class Application extends React.Component<EnhancedProps, State> {
  public readonly state: State = {
    confirmationCallback: () => {},
    confirmationMessage: '',
    isConfirmationPromptOpen: false
  };

  public render() {
    const {
      confirmationCallback,
      confirmationMessage,
      isConfirmationPromptOpen
    } = this.state;

    return (
      <ReduxProvider store={ReduxService.store}>
        <ApolloProvider client={ApolloService.client}>
          <MuiThemeProvider theme={theme}>
            <I18nextProvider i18n={I18nextService.i18n}>
              <>
                <Router getUserConfirmation={this.handleGetUserConfirmation}>
                  <Switch>
                    <Route path="/sign-in" component={SessionRoute} />
                    <Route path="/forgot-password" component={SessionRoute} />
                    <Route
                      path="/reset-password/:token"
                      component={SessionRoute}
                    />
                    <Route
                      path="/register/:invitationId"
                      component={SessionRoute}
                    />
                    <Route path="/onboarding" component={SessionRoute} />
                    <Route path="/guest" component={GuestRoute} />
                    <Route path="/404" component={NotFound} />
                    <Route path="/" component={AuthenticatedRoute} />
                  </Switch>
                </Router>

                <RotateDevicePlaceholder />

                <ConfirmationPrompt
                  isOpen={isConfirmationPromptOpen}
                  message={confirmationMessage}
                  onConfirm={confirmationCallback}
                />
              </>
            </I18nextProvider>
          </MuiThemeProvider>
        </ApolloProvider>
      </ReduxProvider>
    );
  }

  private handleGetUserConfirmation = (
    confirmationMessage: string,
    callback: ConfirmationCallback
  ) => {
    const confirmationCallback = (ok: boolean) => {
      callback(ok);
      this.setState(() => ({isConfirmationPromptOpen: false}));
    };

    this.setState(() => ({
      confirmationCallback,
      confirmationMessage,
      isConfirmationPromptOpen: true
    }));
  };
}

export default withStyles(styles)(Application);



// WEBPACK FOOTER //
// ./src/ui/application/application.tsx